import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getMapConfiguration } from '@ngsos/portal-core/agency';
import { projectIncidentLocationsForMap } from '@ngsos/portal-core/incidents';

import type { LeafletMap, TileLayer } from '@/lib/leaflet/leafletMap';
import { mapyCzLayers } from '@/features/shared/tileLayers';
import { accuracyColor, NgSwitch, PropertyWatcher } from '@/ui';
import { useAgencyConfiguration } from '@/app/shell/agencyConfiguration';
import { isMeasuredLocation, LocationSource } from '@/signalr';
import type {
  LocationSource as PortalLocationSource,
  PermanentResidence,
  CoordinateLocation,
  Location,
} from '@/signalr';
import {
  changeCallerLocationFromMap,
  changePreferredCallerLocationSource,
  openManualGpsCoordinatesDialog,
} from '../actions';
import { CONTEXT_MENU_CHANGE_CALLER_LOCATION } from './mapModel';
import type { ContextMenuItem, LatLng, MapCircle, MapPath } from './mapModel';
import { callerLocationMarker, createMarkerBySource, homeMarker } from './markers';
import type { MapMarker } from './markers';
import { LeafletMapView } from './LeafletMapView';
import { LeafletMapWithPopups } from './LeafletMapWithPopups';
import { MapLayerControl } from './MapLayerControl';
import { IncidentDetailMarkerFilters } from './IncidentDetailMarkerFilters';
import { PopupView } from './templates/PopupView';
import { CallerCurrentPositionMarkerDetailTemplate } from './templates/CallerCurrentPositionMarkerDetailTemplate';
import { CallerPermanentResidenceMarkerDetailTemplate } from './templates/CallerPermanentResidenceMarkerDetailTemplate';

export type IncidentDetailMapComponentProps = {
  incidentId: string;
  center?: { latitude: number; longitude: number } | null;
  zoom?: number | null;
  callerPosition: Location | null;
  callerPositionHistory: readonly Location[];
  lastLocationBySource: readonly Location[];
  permanentResidence?: PermanentResidence | null;
  onChangeCallerLocation?: (coordinates: { latitude: number; longitude: number }) => void | Promise<void>;
  incidentIsReadOnly: boolean;
};

type MapPopup =
  { kind: 'none' } | { kind: 'homePosition' } | { kind: 'callerPositionSource'; locationSource?: PortalLocationSource };

const CIRCLE_COLOR_GOOD = '#009A38';
const CIRCLE_COLOR_POOR = '#FFB500';
const CIRCLE_COLOR_USELESS = '#DE000F';

function getCircleByAccuracy(position: CoordinateLocation): MapCircle | null {
  if (!isMeasuredLocation(position) || position.accuracy === null) {
    return null;
  }

  const quality = accuracyColor(position.accuracy);
  const color =
    quality === 'excellent' ? CIRCLE_COLOR_GOOD : quality === 'warning' ? CIRCLE_COLOR_POOR : CIRCLE_COLOR_USELESS;

  return {
    latLng: { lat: position.latitude, lng: position.longitude },
    radius: position.accuracy,
    color,
    opacity: 0.5,
    fillOpacity: 0.2,
    testID: 'incident-map-accuracy-circle',
  };
}

export function IncidentDetailMapComponent({
  incidentId,
  center,
  zoom,
  callerPosition,
  callerPositionHistory,
  lastLocationBySource,
  permanentResidence,
  onChangeCallerLocation,
  incidentIsReadOnly,
}: IncidentDetailMapComponentProps) {
  const { t, i18n } = useTranslation();
  const agencyConfiguration = useAgencyConfiguration();
  const mapConfiguration = getMapConfiguration(agencyConfiguration);

  const leafletMapRef = useRef<LeafletMap | null>(null);

  const [openedPopup, setOpenedPopup] = useState<MapPopup>({ kind: 'none' });
  const [showOtherLocationSourcesSwitch, setShowOtherLocationSourcesSwitch] = useState(false);

  const language = (i18n.language || 'en').split('-')[0] ?? 'en';
  const tileLayers: TileLayer[] = useMemo(
    () => mapyCzLayers(language, mapConfiguration.mapyCzApiKey, t),
    [language, mapConfiguration.mapyCzApiKey, t],
  );
  const [activeTileLayerId, setActiveTileLayerId] = useState(
    () => (tileLayers.find((layer) => layer.isDefault) ?? tileLayers[0])?.id ?? '',
  );
  const [visibleOverlayIds, setVisibleOverlayIds] = useState<ReadonlySet<string>>(
    () => new Set(mapConfiguration.layers.filter((layer) => layer.isDefaultVisible).map((layer) => layer.id)),
  );

  const locationProjection = useMemo(
    () => projectIncidentLocationsForMap(callerPosition, callerPositionHistory, lastLocationBySource),
    [callerPosition, callerPositionHistory, lastLocationBySource],
  );
  const callerPositionBySource = useMemo(
    () => new Map(locationProjection.contextualLastLocations.map((location) => [location.source, location])),
    [locationProjection.contextualLastLocations],
  );

  const showOtherLocationSources =
    (callerPositionBySource.size > 0 && openedPopup.kind === 'callerPositionSource') || showOtherLocationSourcesSwitch;
  const disableShowOtherLocationSources = callerPositionBySource.size === 0;

  const markers: MapMarker[] = useMemo(() => {
    const result: MapMarker[] = [];

    if (permanentResidence?.coordinates != null) {
      result.push(
        homeMarker('HomeMarker', {
          lat: permanentResidence.coordinates.latitude,
          lng: permanentResidence.coordinates.longitude,
        }),
      );
    }

    if (locationProjection.selectedCurrentLocation !== null) {
      result.push(
        callerLocationMarker('CallerLocationMarker', {
          lat: locationProjection.selectedCurrentLocation.latitude,
          lng: locationProjection.selectedCurrentLocation.longitude,
        }),
      );
    }

    if (showOtherLocationSources) {
      for (const positionBySource of callerPositionBySource.values()) {
        result.push(createMarkerBySource(positionBySource));
      }
    }

    return result;
  }, [
    permanentResidence,
    locationProjection.selectedCurrentLocation,
    showOtherLocationSources,
    callerPositionBySource,
  ]);

  const circles: MapCircle[] = useMemo(() => {
    if (showOtherLocationSources) {
      const visibleLocations = [
        ...(locationProjection.selectedCurrentLocation === null ? [] : [locationProjection.selectedCurrentLocation]),
        ...callerPositionBySource.values(),
      ];
      return visibleLocations.map(getCircleByAccuracy).filter((circle): circle is MapCircle => circle !== null);
    }
    const selectedCircle =
      locationProjection.selectedCurrentLocation === null
        ? null
        : getCircleByAccuracy(locationProjection.selectedCurrentLocation);
    return selectedCircle === null ? [] : [selectedCircle];
  }, [showOtherLocationSources, callerPositionBySource, locationProjection.selectedCurrentLocation]);

  const paths: MapPath[] = useMemo(() => {
    if (locationProjection.selectedSource !== LocationSource.Entered && locationProjection.selectedTrack.length > 1) {
      const callerLocations: LatLng[] = locationProjection.selectedTrack.map((position) => ({
        lat: position.latitude,
        lng: position.longitude,
      }));

      const outerPathOptions = { weight: 7, stroke: true, color: '#444444', opacity: 0.7 };
      const innerPathOptions = { weight: 3, stroke: true, color: '#999999', opacity: 0.7 };

      return [
        { points: callerLocations, options: outerPathOptions },
        { points: callerLocations, options: innerPathOptions },
      ];
    }

    return [];
  }, [locationProjection.selectedSource, locationProjection.selectedTrack]);

  const contextMenuItems: ContextMenuItem[] = incidentIsReadOnly
    ? []
    : [{ id: CONTEXT_MENU_CHANGE_CALLER_LOCATION, text: t('Map_ContextMenuItem_SetCallerLocation') }];

  const closePopup = () => {
    setOpenedPopup({ kind: 'none' });
  };

  const openCallerPositionSourcePopup = (locationSource?: PortalLocationSource) => {
    setOpenedPopup({ kind: 'callerPositionSource', locationSource });
  };

  const onMarkerClicked = (markerId: string) => {
    const marker = markers.find((candidate) => candidate.markerId === markerId);
    if (marker === undefined) {
      return;
    }

    if (marker.kind === 'callerLocation' || marker.locationSource !== undefined) {
      openCallerPositionSourcePopup(locationProjection.selectedCurrentLocation?.source ?? marker.locationSource);
      return;
    }

    setOpenedPopup({ kind: marker.kind === 'home' ? 'homePosition' : 'none' });
  };

  const popupLocationSource =
    openedPopup.kind === 'callerPositionSource'
      ? (openedPopup.locationSource ??
        locationProjection.selectedCurrentLocation?.source ??
        callerPositionBySource.keys().next().value)
      : undefined;
  const popupCurrentLocation =
    [locationProjection.selectedCurrentLocation, ...locationProjection.contextualLastLocations].find(
      (location) => location?.source === popupLocationSource,
    ) ?? null;
  const popupAvailableLocations = [
    ...(locationProjection.selectedCurrentLocation === null ? [] : [locationProjection.selectedCurrentLocation]),
    ...locationProjection.contextualLastLocations,
  ];

  const changePreferredLocationSource = async (preferredLocationSource: PortalLocationSource) => {
    await changePreferredCallerLocationSource(incidentId, preferredLocationSource);
  };

  const changeCallerLocation = async (latLng: LatLng) => {
    if (onChangeCallerLocation) {
      if (!disableShowOtherLocationSources) {
        setShowOtherLocationSourcesSwitch(true);
      }
      await onChangeCallerLocation({ latitude: latLng.lat, longitude: latLng.lng });
    } else {
      await changeCallerLocationFromMap(incidentId, { latitude: latLng.lat, longitude: latLng.lng });
    }
  };

  const openManualGpsEditDialog = () => {
    openManualGpsCoordinatesDialog(incidentId);
  };

  const toggleLocationSourcesSwitch = () => {
    if (showOtherLocationSources) {
      setShowOtherLocationSourcesSwitch(false);
      if (openedPopup.kind === 'callerPositionSource') {
        closePopup();
      }
    } else {
      setShowOtherLocationSourcesSwitch(true);
      openCallerPositionSourcePopup();
    }
  };

  const centerMapOnCallerPosition = () => {
    const leafletMap = leafletMapRef.current;
    if (leafletMap !== null) {
      let positions: CoordinateLocation[] =
        locationProjection.selectedCurrentLocation === null ? [] : [locationProjection.selectedCurrentLocation];
      if (showOtherLocationSources) {
        positions = positions.concat([...callerPositionBySource.values()]);
      }

      if (positions.length === 0) {
        return;
      }

      const defaultPadding: [number, number] = [50, 50];
      const paddingTopLeft: [number, number] =
        openedPopup.kind === 'callerPositionSource' ? [350, 350] : defaultPadding;
      leafletMap.ensurePointsVisible(
        positions.map((position) => [position.latitude, position.longitude]),
        paddingTopLeft,
        defaultPadding,
      );
    }
  };

  const showOtherLocationSourcesChange = () => {
    if (showOtherLocationSources) {
      centerMapOnCallerPosition();
    }
  };

  const handleTileLayerChanged = (layerId: string) => {
    setActiveTileLayerId(layerId);
    leafletMapRef.current?.setActiveTileLayer(layerId);
  };

  const handleOverlayToggled = (id: string, visible: boolean) => {
    setVisibleOverlayIds((current) => {
      const next = new Set(current);
      if (visible) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });

    leafletMapRef.current?.setOverlayVisibility(id, visible);
  };

  return (
    <>
      <LeafletMapWithPopups
        mapContent={
          <LeafletMapView
            onMapReady={(map) => {
              leafletMapRef.current = map;
            }}
            center={center}
            zoom={zoom}
            markers={markers}
            circles={circles}
            paths={paths}
            onMarkerClicked={onMarkerClicked}
            contextMenuItems={contextMenuItems}
            onChangeCallerLocation={(position) => void changeCallerLocation(position)}
            agencyLayers={mapConfiguration.layers}
            tileLayers={tileLayers}
            hideBuiltInLayerControl
          />
        }
        popupContent={
          <>
            <PopupView show={openedPopup.kind === 'callerPositionSource'} onClose={closePopup}>
              {popupCurrentLocation !== null && (
                <CallerCurrentPositionMarkerDetailTemplate
                  currentPosition={popupCurrentLocation}
                  positions={popupAvailableLocations}
                  onSelect={changePreferredLocationSource}
                  allowSourceSwitching={!incidentIsReadOnly}
                  openManualPositionDialog={openManualGpsEditDialog}
                />
              )}
            </PopupView>

            <PopupView show={openedPopup.kind === 'homePosition'} onClose={closePopup}>
              <CallerPermanentResidenceMarkerDetailTemplate item={permanentResidence} />
            </PopupView>
          </>
        }
      />

      <div className="position-absolute z-2 top-0 d-flex flex-row gap-1 m-2" style={{ right: '67px' }}>
        <IncidentDetailMarkerFilters>
          <NgSwitch
            label={t('IncidentDetail_ShowLocationSources')}
            onValueChanged={toggleLocationSourcesSwitch}
            value={showOtherLocationSources}
            disabled={disableShowOtherLocationSources}
          />
        </IncidentDetailMarkerFilters>

        <MapLayerControl
          tileLayers={tileLayers}
          overlayLayers={mapConfiguration.layers}
          activeTileLayerId={activeTileLayerId}
          onTileLayerChanged={handleTileLayerChanged}
          visibleOverlayIds={visibleOverlayIds}
          onOverlayToggled={handleOverlayToggled}
        />
      </div>

      <PropertyWatcher value={showOtherLocationSources} onChange={showOtherLocationSourcesChange} />
    </>
  );
}
